<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageRooms.title') }}
    </portal>
    <template v-if="rooms !== null">
      <card :title="`${$t('admin.pageRooms.card.rooms')} (${rooms.filter(it => it.deleted === false).length})`" cols="12">
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <draggable v-model="rooms" group="rooms">
              <hm-card-inner-line v-for="room in rooms"
                                  v-if="!room.deleted"
                                  :title="getName(room)"
                                  :clickable="true"
                                  :key="room.id"
                                  @click="routeToRoom(room)" />
            </draggable>
          </hm-col>
        </hm-card-inner>
      </card>

      <card v-if="!isEmpty(deletedRooms)" :title="$t('admin.pageRooms.card.deletedRooms')" cols="12" card-type="collapsible">
        <hm-card-inner :full-size="true">
          <hm-card-inner-line v-for="room in deletedRooms"
                              :title="getName(room)"
                              :clickable="true"
                              :key="room.id"
                              @click="routeToRoom(room)" />
        </hm-card-inner>
      </card>
    </template>

    <modal v-if="showCreateRoom"
           :close-on-click-outside="false"
           :hide-on-submit="false"
           :title="$t('admin.pageRooms.card.createRooms')"
           :confirm-label="$t('admin.pageRooms.confirmCreateRooms')"
           @confirm="createRooms"
           @cancel="hideCreateRooms">
      <hm-card-inner :title="$t('admin.pageRooms.card.roomNumber')">
        <hm-input v-if="isNumber(newRooms.start)"
                  v-model="newRooms.start"
                  :lazy="true"
                  :label="$t('admin.pageRooms.start')"
                  name="start" />
        <hm-input v-else
                  v-model="newRooms.start"
                  :lazy="true"
                  :label="$t('admin.pageRooms.start')"
                  name="start" />
        <hm-input-number v-if="isNumber(newRooms.start)"
                         ref="roomCount"
                         v-model="newRooms.count"
                         :lazy="true"
                         :label="$t('admin.pageRooms.count')"
                         validate="max_value:20|min_value:1"
                         name="count" />
        <hm-input-select ref="roomType"
                         v-model="newRooms.roomType"
                         :label="$t('admin.pageRooms.roomType')"
                         validate="required"
                         name="roomType">
          <option v-for="item in hotelSettings.roomTypes.all(newRooms.roomType)" v-if="!item.deleted" :value="item.id" v-html="item.name[$shortLocale]"></option>
        </hm-input-select>
      </hm-card-inner>
      <hm-card-inner v-if="isNumber(newRooms.start) && newRooms.count > 1" :title="$t('admin.pageRooms.card.createRoomOptions')">
        <hm-input v-model="newRooms.prefix" :lazy="true" :label="$t('admin.pageRooms.prefix')" name="prefix" />
        <hm-input v-model="newRooms.suffix" :lazy="true" :label="$t('admin.pageRooms.suffix')" name="suffix" />
        <hm-input v-model="newRooms.digits"
                  :lazy="true"
                  :label="$t('admin.pageRooms.digits')"
                  :help="$t('admin.pageRooms.digits_help')"
                  name="digits" />
      </hm-card-inner>
      <hm-card-inner :title="$t('admin.pageRooms.card.roomsToCreate')">
        <hm-col cols="12">
          <ul>
            <li v-for="roomNumber in newRoomNumbers" :key="roomNumber" v-html="roomNumber"></li>
          </ul>
        </hm-col>
      </hm-card-inner>
    </modal>

    <hm-floating-buttons id="actions" ref="floatingButtons">
      <hm-floating-button :label="$t('admin.pageRooms.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('admin.pageRooms.actions.createMultipleRooms')"
                                  :disabled="hotelSettings.hotel.licensedRooms <= roomCount && !hasRole('ROLE_SYSADMIN')"
                                  icon="plus"
                                  color="blue"
                                  @click="showCreateRooms(3)" />
        <hm-floating-child-button :label="$t('admin.pageRooms.actions.createSingleRoom')"
                                  :disabled="hotelSettings.hotel.licensedRooms <= roomCount && !hasRole('ROLE_SYSADMIN')"
                                  icon="plus"
                                  color="blue"
                                  @click="showCreateRooms(1)" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, groupBy, isEmpty, isNil, toNumber, sortBy, trim, padStart, isString } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmCardInner from '../../lib/layout/HmCardInner';

export default {
  name: 'PageRooms',

  components: {
    HmCardInner,
    draggable,
  },

  autosave: [
    {
      name: 'rooms',
      variable: 'sortedRoomIds',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const update = {
          roomIds: this.sortedRoomIds,
        };

        this.axios.put(this.serviceUrls.room.sort(this, this.$route.params.roomId), update)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.$serviceBus.$emit('room.list', this.ratePlans);
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      rooms: null,
      showCreateRoom: false,
      newRooms: {
        prefix: null,
        suffix: null,
        roomType: null,
        start: 1,
        count: 1,
        digits: 0,
      },
    };
  },

  computed: {
    deletedRooms: function () {
      return isNil(this.rooms) ? null : this.rooms.filter(it => it.deleted === true);
    },
    sortedRoomIds: function () {
      return isNil(this.rooms) ? null : this.rooms.map(it => it.id);
    },
    roomCount: function () {
      return isNil(this.rooms) ? 0 : this.rooms.filter(it => it.deleted !== true).length;
    },
    newRoomNumbers: function () {
      const ret = [];

      if (isNil(this.newRooms.start) || trim(this.newRooms.start).length === 0) return ret;

      if (isString(this.newRooms.start) && this.newRooms.start.includes(',')) {
        this.newRooms.start.split(',').map(it => it.trim()).filter(it => !isEmpty(it)).forEach(it => ret.push(it));
      } else {
        const start = this.isNumber(this.newRooms.start) ? this.newRooms.start : 1;
        const prefix = isNil(this.newRooms.prefix) ? '' : this.newRooms.prefix;
        const suffix = isNil(this.newRooms.suffix) ? '' : this.newRooms.suffix;
        for (let i = start; i < (toNumber(start) + toNumber(this.newRooms.count)); i++) {
          const it = this.isNumber(this.newRooms.start) ? i : this.newRooms.start;
          const roomNumber = `${prefix}${padStart(it, this.newRooms.digits, '0')}${suffix}`;
          ret.push(roomNumber);
        }
      }
      return ret;
    },
  },

  watch: {
    'newRooms.start': function (newVal) {
      if (!this.isNumber(newVal)) {
        this.newRooms.count = 1;
      }
    },
    roomCount: function () {
      if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && this.hotelSettings.hotel.licensedRooms < this.roomCount) {
        this.showModal(`The actual room count (${this.roomCount}) exceeds the number of licensed rooms (${this.hotelSettings.hotel.licensedRooms}). `);
      }
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isNumber(val) {
      // eslint-disable-next-line no-restricted-globals
      return !isNaN(toNumber(val));
    },
    loadData() {
      this.axios.get(this.serviceUrls.room.list(this))
        .then((response) => {
          this.rooms = response.data.data;
          this.$serviceBus.$emit('room.list', this.rooms);
        });
    },
    showCreateRooms(count = 1) {
      this.newRooms = {
        prefix: null,
        suffix: null,
        roomType: null,
        start: 1,
        count: count,
        digits: 0,
      };
      this.showCreateRoom = true;
      // this.$nextTick(() => {
      //   this.$refs.roomType.$validator.validateAll();
      // });
    },
    hideCreateRooms() {
      this.showCreateRoom = false;
    },
    createRooms() {
      if (!isNil(this.$refs.roomCount)) this.$refs.roomCount.$v.$touch();
      if (!isNil(this.$refs.roomType)) this.$refs.roomType.$v.$touch();

      if (!isNil(this.$refs.roomCount) && this.$refs.roomCount.$v.$anyError) return;
      if (!isNil(this.$refs.roomType) && this.$refs.roomType.$v.$anyError) return;


      if (isEmpty(this.newRooms.roomType)) {
        this.showErrorMessage(this.$t('admin.pageRooms.createRooms.error.roomTypeMustBeSelected'));
      }
      if (isEmpty(this.newRoomNumbers)) {
        this.showWarningMessage(this.$t('admin.pageRooms.createRooms.error.noRoomsToCreate'));
      }

      Promise.all(this.newRoomNumbers.map(it => this.addRoom(it, this.newRooms.roomType)))
        .then(() => {
          this.showSuccessMessage();
          this.loadData();
        });

      this.hideCreateRooms();
    },
    addRoom(roomNumber, roomType) {
      return this.axios.post(this.serviceUrls.room.list(this), {
        roomNumber: roomNumber,
        name: roomNumber,
        description: null,
        roomTypeId: roomType,
        emptyLinesAfter: 0,
        sort: 999,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    getName(room) {
      const name = isNil(room) ? null : room.roomNumber;
      if (isNil(name)) return '-';
      let ret = null;

      ret = room.roomNumber;

      let details = '';

      if (room.deleted === false) {
        if (!isEmpty(room.name) && room.name !== room.roomNumber) {
          details += `, ${room.name}`;
        }

        const roomType = this.findHotelSettingById('roomTypes', room.roomTypeId);
        details += `, ${roomType.name[this.hotelSettings.details.primaryLanguage]}  (${roomType.minOccupancy} - ${roomType.maxOccupancy} Pers.)`;

        if (!isEmpty(room.description)) {
          details += `, ${room.description}`;
        }
      } else {
        const roomNumberParts = ret.split('::');
        if (roomNumberParts.length >= 2) ret = roomNumberParts[1];
      }

      if (!isNil(ret)) {
        ret = ret.trim();
      }

      if (isEmpty(ret)) {
        ret = '-';
      }

      ret = `${ret}<span class="color--gray">${details}</span>`;

      if (!isEmpty(ret)) return ret;

      return name[this.hotelSettings.details.primaryLanguage];
    },
    routeToRoom(room) {
      this.$router.push({
        name: 'room',
        params: {
          roomId: room.id,
        },
      });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageRooms.vue